import {
  useQueryAnalyticsFeedback,
  useQueryAnalyticsSummary,
  useQueryAnalyticsTop,
} from "@apis:analytics/analytics";
import { Header } from "@components:Header";
import { SVGInternalIcon } from "@components:SVGInternalIcon";
import { SwitchPage } from "@components:SwitchPage";
import { useBotSelection } from "@hooks:useBotSelection";
import { BotSelection } from "@modules:BotSelection/BotSelection";
import { Box, Chip } from "@mui/material";
import Typography from "@mui/material/Typography";
import {
  axisClasses,
  chartsAxisHighlightClasses,
  lineElementClasses,
} from "@mui/x-charts";
import { DateRangeSelect } from "@pages:app/analytics/components/DateRangeSelect";
import { Feedback } from "@pages:app/analytics/components/Feedback";
import { Overview } from "@pages:app/analytics/components/Overview";
import { PeriodLineChart } from "@pages:app/analytics/components/PeriodLineChart";
import { TopFive } from "@pages:app/analytics/components/TopFive";
import { summarySelector } from "@pages:app/analytics/selectors";
import {
  AccountColumns,
  generateActiveUsersChart,
  generateRequestsChart,
  getRangeValues,
  RangeType,
  ReferencedDocumentColumns,
  RequestTypeList,
} from "@pages:app/analytics/utils";
import { useEffect, useState } from "react";

import { RequestType } from "@:openapi";

export const Analytics = () => {
  const { getSelectedBot } = useBotSelection();

  const persistentBot = getSelectedBot();

  const [selectedBot, setSelectedBot] = useState<string>(
    persistentBot?.id as string,
  );

  const [currentRangeType, setCurrentRangeType] = useState<RangeType>(
    RangeType.LastSevenDays,
  );
  const [range, setRange] = useState<Array<string>>(
    getRangeValues()[currentRangeType] as Array<string>,
  );
  const [tempRange, setTempRange] = useState<{
    fromDate: string | null;
    toDate: string | null;
  }>({
    fromDate: null,
    toDate: null,
  });

  const [currentRequestType, setCurrentRequestType] =
    useState<RequestType>("all");

  useEffect(() => {
    if (tempRange.fromDate && tempRange.toDate) {
      setRange([tempRange.fromDate, tempRange.toDate]);
    }
  }, [tempRange]);

  const analyticsSummaryQuery = useQueryAnalyticsSummary(
    {
      botId: selectedBot,
      fromDate: range[0],
      toDate: range[1],
      requestType: currentRequestType,
    },
    {
      enabled: !!selectedBot,
      select: summarySelector,
    },
  );

  const analyticsTopUserRequestsQuery = useQueryAnalyticsTop(
    {
      botId: selectedBot,
      fromDate: range[0],
      toDate: range[1],
      topType: "userRequests",
      requestType: currentRequestType,
    },
    {
      enabled: !!selectedBot && !!range?.[0] && !!range?.[1],
    },
  );

  const analyticsTopReferencedDocumentsQuery = useQueryAnalyticsTop(
    {
      botId: selectedBot,
      fromDate: range[0],
      toDate: range[1],
      topType: "documents",
    },
    {
      enabled: !!selectedBot && !!range?.[0] && !!range?.[1],
    },
  );

  const analyticsFeedbackQuery = useQueryAnalyticsFeedback(
    {
      botId: selectedBot,
      fromDate: range[0],
      toDate: range[1],
    },
    {
      enabled: !!selectedBot && !!range?.[0] && !!range?.[1],
    },
  );

  const activeUsersChartInfo = generateActiveUsersChart(
    range[0],
    range[1],
    analyticsSummaryQuery.data?.activeUsersPeriodData,
  );
  const numberOfRequestsChartInfo = generateRequestsChart(
    range[0],
    range[1],
    analyticsSummaryQuery.data?.requestsPeriodData,
  );

  return (
    <Box
      sx={{
        background: (theme) => theme.palette.background.default,
        minHeight: "100vh",
        padding: "12px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "30px",
          background: (theme) => theme.palette.common.white,
        }}
      >
        <Header
          sx={{
            background: (theme) => theme.palette.common.white,
            borderTopLeftRadius: "30px",
            borderTopRightRadius: "30px",
          }}
          rightSide={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  paddingX: "0.5rem",
                }}
              >
                <BotSelection
                  value={selectedBot}
                  noMatchText={"No bot"}
                  onChange={(botId) => setSelectedBot(botId)}
                />
              </Box>
              <SwitchPage />
            </Box>
          }
          leftSide={
            <Box
              sx={{
                marginLeft: "1.5rem",
              }}
            >
              <SVGInternalIcon
                icon="nemoIcon"
                sx={{
                  width: "120px",
                  height: "120px",
                }}
              />
            </Box>
          }
        />
        <Box
          sx={{
            flex: "1 1 0% !important",
            textAlign: "center",
            paddingY: "3%",
          }}
        >
          <Typography
            sx={{
              color: (theme) => theme.palette.text.primary,
              fontWeight: "bold",
              fontSize: "32px",
              lineHeight: "32px",
            }}
          >
            Bot usage analytics
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              mx: "auto",
              alignItems: "center",
              paddingX: "1rem",
              marginTop: "3.5rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                fontWeight: "600",
                lineHeight: "1.75rem",
                fontSize: "0.875rem",
                columnGap: "2rem",
              }}
            >
              {Object.values(RangeType).map((rangeType) => {
                if (rangeType === RangeType.Custom) {
                  return (
                    <DateRangeSelect
                      key={rangeType}
                      sx={{
                        minWidth: "100px",
                        backgroundColor:
                          rangeType === currentRangeType
                            ? "#1FADB2"
                            : "#F0FAFA",
                        color:
                          rangeType === currentRangeType ? "white" : undefined,
                        "&:hover": {
                          backgroundColor: "#1FADB2",
                          color: "white",
                          boxShadow: "0px 8px 24px 0px #1DA5AB26",
                        },
                        boxShadow:
                          rangeType === currentRangeType
                            ? "0px 8px 24px 0px #00000029;"
                            : "none",
                      }}
                      onClick={() => {
                        setCurrentRangeType(rangeType);
                      }}
                      onChangeFromDate={(value) => {
                        setTempRange((prevState) => {
                          return {
                            ...prevState,
                            fromDate: value?.toISODate() as string,
                          };
                        });
                      }}
                      onChangeToDate={(value) => {
                        setTempRange((prevState) => {
                          return {
                            ...prevState,
                            toDate: value?.toISODate() as string,
                          };
                        });
                      }}
                      fromDate={tempRange.fromDate}
                      toDate={tempRange.toDate}
                    />
                  );
                }
                return (
                  <Chip
                    key={rangeType}
                    sx={{
                      minWidth: "100px",
                      backgroundColor:
                        rangeType === currentRangeType ? "#1FADB2" : "#F0FAFA",
                      color:
                        rangeType === currentRangeType ? "white" : undefined,
                      "&:hover": {
                        backgroundColor: "#1FADB2",
                        color: "white",
                        boxShadow: "0px 8px 24px 0px #1DA5AB26",
                      },
                      boxShadow:
                        rangeType === currentRangeType
                          ? "0px 8px 24px 0px #00000029;"
                          : "none",
                    }}
                    onClick={() => {
                      setCurrentRangeType(rangeType);
                      setRange(getRangeValues()[rangeType]);
                      setTempRange({
                        fromDate: null,
                        toDate: null,
                      });
                    }}
                    label={rangeType}
                  />
                );
              })}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                fontWeight: "600",
                lineHeight: "1.75rem",
                fontSize: "0.875rem",
                columnGap: "2rem",
              }}
            >
              {RequestTypeList.map(({ value, label }) => {
                return (
                  <Chip
                    key={value}
                    sx={{
                      border: ".8px solid #E4F2F2",
                      minWidth: "100px",
                      backgroundColor:
                        value === currentRequestType ? "#1FADB2" : "#F0FAFA",
                      color: value === currentRequestType ? "white" : undefined,
                      "&:hover": {
                        backgroundColor: "#1FADB2",
                        color: "white",
                        boxShadow: "0px 8px 24px 0px #1DA5AB26",
                      },
                      boxShadow:
                        value === currentRequestType
                          ? "0px 8px 24px 0px #00000029;"
                          : "none",
                    }}
                    onClick={() => {
                      setCurrentRequestType(value as RequestType);
                    }}
                    label={label}
                  />
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          marginY: "12px",
          display: "flex",
          gap: "12px",
        }}
      >
        <Overview
          numsOfRequestsYesterday={
            analyticsSummaryQuery.data?.requestsYesterday ?? 0
          }
          numsActiveUserYesterday={
            analyticsSummaryQuery.data?.activeUsersYesterday ?? 0
          }
          numsActiveUsersSelectedPeriod={
            analyticsSummaryQuery.data?.activeUsersPeriodDistinct ?? 0
          }
          numsOfRequestsSelectedPeriod={
            analyticsSummaryQuery.data?.requestsPeriod ?? 0
          }
        />
        <Feedback
          total={analyticsFeedbackQuery?.data?.total ?? 0}
          positive={analyticsFeedbackQuery?.data?.positive ?? 0}
          negative={analyticsFeedbackQuery?.data?.negative ?? 0}
        />
      </Box>
      <Box
        sx={{
          paddingX: "1.5rem",
          borderRadius: "30px",
          background: "#161447",
        }}
      >
        <Typography
          sx={{
            color: "white",
            fontSize: "24px",
            fontWeight: "700",
            lineHeight: "26px",
            marginTop: "2rem",
            marginLeft: "3rem",
          }}
        >
          Active users
        </Typography>
        <PeriodLineChart
          sx={() => ({
            [`.${axisClasses.root}`]: {
              [`.${axisClasses.tick}, .${axisClasses.line}`]: {
                stroke: "white",
              },
              [`.${axisClasses.tickLabel}`]: {
                fill: "white",
              },
            },
            [`.${lineElementClasses.root}`]: {
              strokeWidth: 3,
            },
            [`.${chartsAxisHighlightClasses.root}`]: {
              stroke: "white",
              strokeWidth: 2,
            },
          })}
          xAxis={activeUsersChartInfo.xAxis}
          yAxis={activeUsersChartInfo.yAxis}
          series={activeUsersChartInfo.series}
        />
      </Box>

      <Box
        sx={{
          marginY: "12px",
          borderRadius: "30px",
          background: "white",
          paddingX: "1.5rem",
        }}
      >
        <Typography
          sx={{
            fontSize: "24px",
            fontWeight: "700",
            lineHeight: "26px",
            marginTop: "2rem",
            marginLeft: "3rem",
            color: "#161447",
          }}
        >
          Number of requests
        </Typography>
        <PeriodLineChart
          sx={() => ({
            [`.${lineElementClasses.root}`]: {
              strokeWidth: 3,
            },
            [`.${chartsAxisHighlightClasses.root}`]: {
              strokeWidth: 2,
            },
          })}
          xAxis={numberOfRequestsChartInfo.xAxis}
          yAxis={numberOfRequestsChartInfo.yAxis}
          series={numberOfRequestsChartInfo.series}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: "12px",
        }}
      >
        <TopFive
          key="top5Users"
          title="Top 5 users by usage"
          columns={AccountColumns}
          rows={analyticsTopUserRequestsQuery.data ?? []}
          getRowId={(row) => row.userId}
        />

        <TopFive
          key="top5Documents"
          title="Top 5 referenced documents"
          columns={ReferencedDocumentColumns}
          rows={analyticsTopReferencedDocumentsQuery.data ?? []}
          getRowId={(row) => row.document}
        />
      </Box>
    </Box>
  );
};
